'use client'

import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'
import Image from 'next/image'
import { Play } from 'lucide-react'
import { createContext, useContext, useRef, useState, type ReactNode } from 'react'
import { LANDING_CTA_OUTLINE } from '@/components/santa-video/landing/cta-classes'
import type { SampleVideoAsset } from '@/lib/santa-video/shop-routes'
import type { VideoStoryId } from '@/server/domain/santa-video/stories'

/**
 * Los vídeos de ejemplo de la landing, uno por historia (cada ficha de
 * producto lleva el suyo). Cada uno nace como póster con un botón «Ver
 * ejemplo» y sólo al pulsarlo se monta su `<video>` (con sonido: el clic es el
 * gesto que el navegador exige para reproducir con audio). Así la landing no
 * pinta un reproductor nativo en frío ni descarga nada del mp4 hasta que se pide.
 *
 * La prueba («Lo que escribes es lo que dice») tiene su propio botón para
 * reproducir el ejemplo de cada historia desde otra sección, así que el estado
 * se comparte con un contexto mínimo, indexado por historia. Ese botón además
 * lleva la vista hasta el vídeo.
 */
interface SampleVideoState {
  isPlaying: (story: VideoStoryId) => boolean
  play: (story: VideoStoryId) => void
  recordPlayback: (story: VideoStoryId) => void
  /** Dónde está el vídeo de cada historia, para llevar la vista hasta él. */
  registerFrame: (story: VideoStoryId, node: HTMLDivElement | null) => void
  scrollToFrame: (story: VideoStoryId) => void
}

const SampleVideoContext = createContext<SampleVideoState | null>(null)

function useSampleVideo(): SampleVideoState {
  const state = useContext(SampleVideoContext)
  if (!state) throw new Error('SampleVideo* debe ir dentro de <SampleVideoProvider>.')
  return state
}

export function SampleVideoProvider({ children, locale }: { children: ReactNode; locale: string }) {
  const [playing, setPlaying] = useState<ReadonlySet<VideoStoryId>>(() => new Set())
  const frameRefs = useRef<Partial<Record<VideoStoryId, HTMLDivElement | null>>>({})
  const recorded = useRef(new Set<VideoStoryId>())
  const recordPlayback = (story: VideoStoryId) => {
    if (recorded.current.has(story)) return
    recorded.current.add(story)
    captureProductEvent(PRODUCT_EVENTS.VIDEO_SAMPLE_PLAYED, { product: 'video', story, locale })
  }
  const play = (story: VideoStoryId) => setPlaying((current) => new Set(current).add(story))
  const isPlaying = (story: VideoStoryId) => playing.has(story)
  const registerFrame = (story: VideoStoryId, node: HTMLDivElement | null) => { frameRefs.current[story] = node }
  const scrollToFrame = (story: VideoStoryId) => frameRefs.current[story]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  return (
    <SampleVideoContext.Provider value={{ isPlaying, play, recordPlayback, registerFrame, scrollToFrame }}>{children}</SampleVideoContext.Provider>
  )
}

/** El botón que reproduce el ejemplo de una historia desde fuera de su ficha: el «outline» festivo de la landing. */
export function PlaySampleButton({ story, label }: { story: VideoStoryId; label: string }) {
  const { play, scrollToFrame } = useSampleVideo()
  return (
    <button
      className={LANDING_CTA_OUTLINE}
      onClick={() => {
        play(story)
        scrollToFrame(story)
      }}
      type="button"
    >
      <Play className="h-5 w-5" /> {label}
    </button>
  )
}

export function SampleVideoPlayer({ story, sample, label, watchLabel }: {
  story: VideoStoryId
  sample: SampleVideoAsset
  label: string
  watchLabel: string
}) {
  const { isPlaying, play, recordPlayback, registerFrame } = useSampleVideo()
  return (
    <div className="relative mx-auto w-full max-w-[300px]" ref={(node) => registerFrame(story, node)}>
      <div className="relative aspect-[9/16] overflow-hidden rounded-3xl border-4 border-white/20 bg-black shadow-2xl">
        {isPlaying(story) ? (
          <video aria-label={label} autoPlay className="h-full w-full object-cover" controls onPlay={() => recordPlayback(story)} playsInline src={sample.src} />
        ) : (
          <>
            <Image alt={label} className="object-cover" fill sizes="300px" src={sample.poster} />
            <button
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/25 transition-colors hover:bg-black/15"
              onClick={() => play(story)}
              type="button"
            >
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-red-700 shadow-2xl transition-transform hover:scale-110">
                <Play className="ml-1 h-9 w-9 fill-current" />
              </span>
              <span className="rounded-full bg-black/50 px-4 py-1.5 text-base font-semibold text-white backdrop-blur-sm">{watchLabel}</span>
            </button>
          </>
        )}
      </div>
    </div>
  )
}
